import { SearchPageBase } from '@common/SearchPageBase';
import { createTableDefaultColumn, createTableLinkWithOpenNewTabColumn } from '@common/tableColumn.services';
import { Column, ComponentType, createTableCell, createTableColumn, ResponsivePaginatedTable } from '@gcl/react-core';
import { routes } from '@routes/routes.models';
import * as React from 'react';
import '@gcl/react-core/dist/themes.scss';
import { ISortByQuery } from '@common/api.models';
import { useEffect, useMemo } from 'react';
import { AppConfigContext } from '@config/AppConfig';
import { SearchFilter } from '@common/searchFilter/SearchFilter';
import { IFilterFieldConfig, SearchFilterType } from '@common/searchFilter/searchFilter.models';
import { useSearchFilterUrlParam } from '@common/searchFilter/searchFilterUrlParam.hooks';
import { NumberParam } from 'use-query-params';
import { useNavigate } from '@routes/routes.hooks';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { useFetchProjectRequestSearch } from './projectRequest.queries';
import { IProjectRequest, projectRequestSearchKeys, ProjectRequestStatusOptions } from './projectRequest.models';
import '@common/Common.scss';

export interface IProjectRequestListProps {}

export const ProjectRequestList: React.FunctionComponent<IProjectRequestListProps> = () => {
  const context = React.useContext(AppConfigContext);

  const DEFAULT_SORT_BY_FIELD_NAME = 'projectNo';

  const projectRequestSearchFieldConfigs = React.useMemo(() => {
    return [
      {
        fieldName: projectRequestSearchKeys.STATUS,
        displayName: 'Status',
        type: SearchFilterType.TextSelect,
        textSelectOptions: ProjectRequestStatusOptions,
        shouldCastNumber: true,
      } as IFilterFieldConfig,
    ];
  }, []);

  const [sortByQuery, setSortByQuery] = React.useState<ISortByQuery | undefined>({
    sortByField: DEFAULT_SORT_BY_FIELD_NAME,
  });

  const { localFilter, setLocalFilter } = useSearchFilterUrlParam(
    {
      [projectRequestSearchKeys.STATUS]: NumberParam,
    },
    projectRequestSearchFieldConfigs,
  );

  const profileId = context.configs.myUserProfile?.id;
  const { navigate, generateRoutePath } = useNavigate();

  const {
    data: projectRequestPagedResult,
    isLoading: isLoadingProjectRequestSearch,
    isRefetching: isRefetchingProjectRequestSearch,
    error: errorProjectRequestSearch,
  } = useFetchProjectRequestSearch({
    profileId,
    ...localFilter,
    [projectRequestSearchKeys.STATUS]: localFilter[projectRequestSearchKeys.STATUS]
      ? [Number(localFilter[projectRequestSearchKeys.STATUS])]
      : [],
  });

  useEffect(() => {
    setSortByQuery(undefined);
  }, [JSON.stringify(projectRequestPagedResult)]);

  const sortedResult = useMemo(() => {
    const sortingKey = sortByQuery?.sortByField;
    if (!sortByQuery) {
      return projectRequestPagedResult?.items;
    }
    const sorted = [...(projectRequestPagedResult?.items ?? [])].sort((a, b) => {
      const aString = (a[sortingKey as keyof IProjectRequest] ?? '').toString();
      const bString = (b[sortingKey as keyof IProjectRequest] ?? '').toString();
      return sortByQuery.isDesc ? bString.localeCompare(aString) : aString.localeCompare(bString);
    });
    return sorted;
  }, [JSON.stringify(sortByQuery), JSON.stringify(projectRequestPagedResult)]);

  // #region Project Request Table Columns
  const projectRequstTableColumns: Column[] = React.useMemo(() => {
    return [
      createTableLinkWithOpenNewTabColumn<IProjectRequest>(
        'projectNo',
        'Project No.',
        ({ item }) => generateRoutePath(routes.projectRequest.view.path, { id: item.id.toString() }),
        ({ value, item, index, cellExtras }, link) => link,
      ),
      createTableDefaultColumn('projectName', 'Project Name'),
      createTableDefaultColumn('description', 'Description'),
      createTableColumn({
        Header: 'Status',
        accessor: 'status',
        Cell: createTableCell<string, any>(
          (value: string, item: IProjectRequest, index: number, cellExtras: { [key: string]: any }) => {
            if (item.processedBy) return <span>Completed</span>;
            return <span>{ProjectRequestStatusOptions.find((status) => status.key === item.status)?.text}</span>;
          },
        ),
      } as Column),
    ];
  }, []);
  // #endregion Project Request Table Columns

  return (
    <SearchPageBase
      data={sortedResult}
      breadcrumbItems={[
        {
          text: 'Home',
          prefixIconName: 'House',
          onClick: () => {
            navigate(routes.announcement.list.path);
          },
        },
        {
          prefixIconName: 'Search',
          text: 'Projects',
          active: true,
        },
      ]}
      title="Project Requests"
      searchFilter={
        <SearchFilter
          fieldConfigs={projectRequestSearchFieldConfigs}
          onFilterChanged={(filter) => {
            setLocalFilter(filter);
          }}
          defaultFilter={localFilter}
        />
      }
      isTableLoading={isLoadingProjectRequestSearch}
      isTableRefetching={isRefetchingProjectRequestSearch}
      buttonNavBarItem={[
        {
          text: 'New',
          navigateToPath: routes.projectRequest.create.path,
          iconName: 'FileEarmarkPlus',
          componentType: ComponentType.Primary,
        },
      ]}
      onRenderTable={(isMenuHidden) => {
        return (
          <>
            <ApiErrorDisplay
              errors={[{ error: errorProjectRequestSearch, genericError: 'Failed to get Project request list' }]}
            />
            <ResponsivePaginatedTable
              classNames={{ body: 'gcl-gems-list-table-body' }}
              columns={projectRequstTableColumns}
              sortingProps={{
                initialSortBy:
                  sortByQuery && sortByQuery.sortByField
                    ? [
                        {
                          id: sortByQuery.sortByField,
                          desc: !!sortByQuery.isDesc,
                        },
                      ]
                    : [],
                onSort: (sortBy) => {
                  if (sortBy && sortBy.length >= 1)
                    setSortByQuery({ sortByField: sortBy[0].id, isDesc: sortBy[0].desc });
                  else setSortByQuery(undefined);
                },
              }}
              disablePagination
              allowSelectRow={false}
              items={sortedResult || []}
              allowExpandRow={false}
            />
          </>
        );
      }}
    />
  );
};
